import { useCallback, useEffect, useRef, useState } from 'react';

import {
  requestHangUp,
  requestTestCall,
  requestTestCallStatus,
  type TestCallStatus,
  type VoiceLine,
} from './validate-api';

export const CALL_ENDED: ReadonlySet<TestCallStatus> = new Set([
  'completed',
  'busy',
  'failed',
  'no-answer',
  'canceled',
]);

/** How the call's state reads to the tester. */
export const callStatusText = (
  status: TestCallStatus | 'placing',
  duration?: number
): string => {
  switch (status) {
    case 'placing':
    case 'queued':
      return 'Placing the call…';
    case 'ringing':
      return 'Ringing your phone…';
    case 'in-progress':
      return 'On the call.';
    case 'completed':
      return duration
        ? `Call ended after ${Math.floor(duration / 60)}:${String(
            duration % 60
          ).padStart(2, '0')}.`
        : 'Call ended.';
    case 'busy':
      return 'Your line was busy.';
    case 'no-answer':
      return 'No answer.';
    case 'canceled':
      return 'Call canceled.';
    default:
      return 'The call failed.';
  }
};

const POLL_MS = 2_000;

export interface TestCallState {
  status: TestCallStatus | 'placing' | null;
  duration?: number;
  error: string | null;
  /** When this call was placed — where its chat is looked for in the logs. */
  placedAt?: number;
}

export const useTestCall = (line: VoiceLine | null) => {
  const [state, setState] = useState<TestCallState>({
    status: null,
    error: null,
  });
  const callSid = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lineRef = useRef(line);
  lineRef.current = line;

  const stopPolling = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const poll = useCallback(() => {
    stopPolling();
    timer.current = setTimeout(async () => {
      const { current: sid } = callSid;
      const { current } = lineRef;
      if (!sid || !current) return;
      try {
        const call = await requestTestCallStatus(current, sid);
        setState((prev) => ({
          ...prev,
          status: call.status,
          duration: call.duration,
          error: null,
        }));
        if (!CALL_ENDED.has(call.status)) poll();
      } catch {
        // A failed poll isn't the call failing, whatever the status: a 401
        // can be the session check failing closed during an auth outage
        // (PSTUDIO-4022: a failed check is not a sign-out), so every failure
        // is retried and the call stays shown as live with its Hang up.
        poll();
      }
    }, POLL_MS);
  }, []);

  const call = useCallback(
    async (to: string) => {
      if (!line) return;
      stopPolling();
      const placedAt = Date.now();
      setState({ status: 'placing', error: null, placedAt });
      try {
        const placed = await requestTestCall(line, to);
        callSid.current = placed.callSid;
        setState({ status: placed.status, error: null, placedAt });
        poll();
      } catch (err) {
        setState({
          status: null,
          error: err instanceof Error ? err.message : 'The call failed.',
        });
        return;
      }
    },
    [line, poll]
  );

  const hangUp = useCallback(async () => {
    const sid = callSid.current;
    if (!sid || !line) return;
    try {
      const ended = await requestHangUp(line, sid);
      setState((prev) => ({
        ...prev,
        status: ended.status,
        duration: ended.duration,
        error: null,
      }));
    } finally {
      stopPolling();
    }
  }, [line]);

  // Leaving the page or switching channel stops watching (the call itself
  // carries on until the tester hangs up).
  useEffect(() => () => stopPolling(), [line]);

  const live =
    state.status !== null &&
    state.status !== 'placing' &&
    !CALL_ENDED.has(state.status);
  return { ...state, live, call, hangUp };
};
